import React from 'react';
import { Phone, Mail, MapPin, Euro } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DevisFileBlock } from './DevisFileBlock';

/**
 * "Informations" tab content for a dossier.
 * Pure view component — all state is owned by the parent DossierPage.
 *
 * Props:
 *   - dossier: dict (with .prospect, .documents, .numero_devis, etc.)
 *   - editMode: bool
 *   - users: list of users for the Commercial dropdown
 *   - onFieldChange(field, value): mutator
 *   - onDevisUploaded(): callback to refetch parent state
 */
export const DossierInfoTab = ({ dossier, editMode, users, onFieldChange, onDevisUploaded }) => {
    const prospect = dossier?.prospect;
    return (
        <div className="bg-white border border-[#E4E4E7] p-6 space-y-6">
            {/* Contact */}
            {prospect && (
                <div>
                    <h3 className="font-semibold text-[#0A0A0A] mb-3">Contact</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-[#E85A24]" />
                            <span>{prospect.telephone || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-[#E85A24]" />
                            <span>{prospect.email || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm col-span-2">
                            <MapPin className="w-4 h-4 text-[#E85A24]" />
                            <span>
                                {prospect.adresse ? `${prospect.adresse}, ` : ''}
                                {prospect.code_postal} {prospect.ville || '-'}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Dossier details */}
            <div className="pt-4 border-t border-[#E4E4E7]">
                <h3 className="font-semibold text-[#0A0A0A] mb-3">Détails du dossier</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="field-label">Type de travaux</Label>
                        {editMode ? (
                            <Input
                                value={dossier.type_travaux || ''}
                                onChange={(e) => onFieldChange('type_travaux', e.target.value)}
                                className="h-10 rounded-sm mt-1"
                            />
                        ) : (
                            <p className="mt-1">{dossier.type_travaux || '-'}</p>
                        )}
                    </div>
                    <div>
                        <Label className="field-label">Montant estimé (€ HT)</Label>
                        {editMode ? (
                            <Input
                                type="number"
                                value={dossier.montant_estime || ''}
                                onChange={(e) => onFieldChange('montant_estime', parseFloat(e.target.value) || null)}
                                className="h-10 rounded-sm mt-1"
                            />
                        ) : (
                            <p className="mt-1 flex items-center gap-1">
                                {dossier.montant_estime ? (
                                    <><Euro className="w-4 h-4" />{dossier.montant_estime.toLocaleString('fr-FR')} € HT</>
                                ) : '-'}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label className="field-label">Montant devis (€ HT)</Label>
                        {editMode ? (
                            <Input
                                type="number"
                                value={dossier.montant_devis || ''}
                                onChange={(e) => onFieldChange('montant_devis', parseFloat(e.target.value) || null)}
                                className="h-10 rounded-sm mt-1"
                            />
                        ) : (
                            <p className="mt-1 flex items-center gap-1">
                                {dossier.montant_devis ? (
                                    <><Euro className="w-4 h-4" />{dossier.montant_devis.toLocaleString('fr-FR')} € HT</>
                                ) : '-'}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label className="field-label">N° de devis</Label>
                        {editMode ? (
                            <Input
                                value={dossier.numero_devis || ''}
                                onChange={(e) => onFieldChange('numero_devis', e.target.value)}
                                placeholder="DEV-2026-0001"
                                className="h-10 rounded-sm mt-1"
                                data-testid="input-numero-devis"
                            />
                        ) : (
                            <p className="mt-1">{dossier.numero_devis || '-'}</p>
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <DevisFileBlock dossier={dossier} onUploaded={onDevisUploaded} />
                    </div>
                    <div>
                        <Label className="field-label">Commercial</Label>
                        {editMode ? (
                            <Select
                                value={dossier.commercial_id || ''}
                                onValueChange={(v) => onFieldChange('commercial_id', v)}
                            >
                                <SelectTrigger className="h-10 rounded-sm mt-1">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.filter((u) => ['commercial', 'manager', 'admin'].includes(u.role)).map((u) => (
                                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <p className="mt-1">{users.find((u) => u.id === dossier.commercial_id)?.name || '-'}</p>
                        )}
                    </div>
                </div>
                {(editMode || dossier.description) && (
                    <div className="mt-4">
                        <Label className="field-label">Description</Label>
                        {editMode ? (
                            <Textarea
                                value={dossier.description || ''}
                                onChange={(e) => onFieldChange('description', e.target.value)}
                                rows={3}
                                className="rounded-sm mt-1"
                            />
                        ) : (
                            <p className="mt-1">{dossier.description || '-'}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
